/**
 * The workspace's subscription to Naurist: starting one, watching it, and
 * calling it off.
 *
 * Every call goes to our own backend, never to the gateway. Razorpay is only
 * ever reached from the page by its SDK, holding the public `key_id` this
 * service is handed — creating a subscription, checking a signature and taking
 * a webhook all need the secret, which is the backend's alone.
 *
 * Money crosses the wire as a decimal string — `"588.82"` — because that is
 * how the backend keeps it. It is a number everywhere on this side.
 *
 * ## Buying one is live, and so is leaving
 *
 * Five calls go to the `/billing/` family, which exists: buying one, reading
 * back what the workspace is on, cancelling it, its invoices, and one invoice
 * as a PDF. So a purchase runs end to end, the Plan details screen is drawn
 * from the record rather than from a guess, and the way out at the foot of it
 * reaches a real endpoint.
 *
 * The refund is live too and is not here: it has an endpoint of its own,
 * `/billing/refund/`, which answers the whole panel as well as taking the
 * request. See `refundService`.
 *
 * What is left — verify, and reading one subscription by id — still points at
 * the older `/api/v1/subscriptions/` family, which does not exist, and stays
 * behind `SUBSCRIPTIONS_LIVE` so those screens do not fire requests into a 404
 * and report a network error to somebody who has done nothing wrong.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api/pagination";
import { ApiError } from "../../../lib/http";
import type { GetToken } from "../../auth/types/auth.types";
import { cycleOfInterval, labelOfInterval } from "./plansService";
import { toQuotedPrice } from "./quoteService";
import type {
  AuthorisationReceipt,
  BoughtSubscriptionResponse,
  BuySubscriptionRequest,
  BuySubscriptionResponse,
  CancelSubscriptionResponse,
  CurrentSubscriptionResponse,
  InvoiceResponse,
  InvoiceStatus,
  PurchasedSubscription,
  RefundResponse,
  RefundStatus,
  Subscription,
  SubscriptionRefund,
  SubscriptionInvoice,
  SubscriptionMethod,
  SubscriptionResponse,
  SubscriptionStatus,
} from "../types/subscriptions.types";
import { CYCLE_LABELS } from "../data/subscriptionPlans";
import type { BillingCycleId } from "../types/billing.types";

/**
 * Whether the endpoints under `API_ENDPOINTS.subscriptions` are built yet.
 *
 * `false` - none of them is. The screens read it to decide whether to ask at
 * all, so the Billing screen answers "never subscribed" and the result page
 * reports what the browser saw rather than confirming it against a 404.
 *
 * It no longer covers buying, reading the plan back, cancelling, the invoice
 * history, an invoice's PDF or the refund: those moved to the billing API and
 * are live, so a purchase, everything the Plan details screen draws and the way
 * out at the foot of it are real however this reads. What is still missing is
 * verify.
 *
 * A constant rather than an env var on purpose: this is not configuration, it
 * is a fact about whether some code exists, and it should change in a commit
 * that can be reviewed.
 */
export const SUBSCRIPTIONS_LIVE = false;

/** What a purchase takes besides the plan: the coupon on the order, and the
 *  usual way to call it off. */
export interface BuyOptions {
  /** The code the quote was last asked with, where the checkout has one. */
  couponCode?: string | null;
  signal?: AbortSignal;
}

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/** Decimal string to number, defended: an absent or unparseable amount lands
 *  on 0 rather than painting "₹ NaN" onto a status card. */
const toAmount = (raw: string | null | undefined): number => {
  const value = Number.parseFloat(raw ?? "");
  return Number.isFinite(value) ? value : 0;
};

/** Every state the backend can send. Anything else is treated as `PENDING` —
 *  a question still open — rather than as a blank card. */
const STATUSES: SubscriptionStatus[] = [
  "CREATED",
  "AUTHENTICATED",
  "ACTIVE",
  "PENDING",
  "HALTED",
  "CANCELLED",
  "COMPLETED",
  "EXPIRED",
];

/**
 * The billing API's own word for a state this app already has a name for.
 *
 * `PENDING_AUTH` is a subscription issued and waiting on a mandate - which is
 * `CREATED` here: nothing signed, nothing charged. Worth translating rather
 * than leaving to the fallback below, because the fallback is `PENDING`, and
 * `PENDING` in this app means a charge that failed and is being retried. One is
 * a purchase that has not started; the other is a payment in trouble, and the
 * status card says very different things about them.
 */
const STATUS_ALIASES: Record<string, SubscriptionStatus> = {
  PENDING_AUTH: "CREATED",
};

const toStatus = (raw: string): SubscriptionStatus => {
  const status = raw?.toUpperCase() as SubscriptionStatus;
  if (STATUS_ALIASES[status]) return STATUS_ALIASES[status];
  return STATUSES.includes(status) ? status : "PENDING";
};

const METHODS: SubscriptionMethod[] = ["UPI", "EMANDATE", "CARD"];

const toMethod = (raw: string | null): SubscriptionMethod | null => {
  const method = raw?.toUpperCase() as SubscriptionMethod;
  return METHODS.includes(method) ? method : null;
};

/** The terms this older record shape can name, by the word it names them with. */
const CYCLE_WORDS: Record<string, BillingCycleId> = {
  monthly: "monthly",
  quarterly: "quarterly",
  "half-yearly": "half-yearly",
  half_yearly: "half-yearly",
  yearly: "yearly",
};

/** A term the price list does not hold reads as monthly — the shortest of
 *  them, so nothing over-states what somebody has committed to. */
const toCycle = (raw: string): BillingCycleId =>
  CYCLE_WORDS[raw?.toLowerCase()] ?? "monthly";

/**
 * Where a refund has got to. Anything unrecognised reads as `PENDING` — a
 * refund the app cannot name is one it should not call finished.
 */
const toRefundStatus = (raw: string): RefundStatus =>
  raw?.toUpperCase() === "PROCESSED" ? "PROCESSED" : "PENDING";

const toRefund = (
  response: RefundResponse | null | undefined
): SubscriptionRefund | null =>
  response
    ? {
        status: toRefundStatus(response.status),
        amount: toAmount(response.amount),
        invoiceNumber: response.invoice_number,
        processedOn: response.processed_at ?? null,
      }
    : null;

/** One subscription, out of the record the endpoint sends. */
export const toSubscription = (
  response: SubscriptionResponse
): Subscription => ({
  id: response.public_id,
  cycle: toCycle(response.cycle),
  // This shape names a cycle rather than an interval, so there is no third
  // term it could be on and the two cards' own words are the whole truth here.
  planName: null,
  cycleLabel: CYCLE_LABELS[toCycle(response.cycle)],
  status: toStatus(response.status),
  method: toMethod(response.method),
  amount: toAmount(response.amount),
  currentStart: response.current_start ?? null,
  currentEnd: response.current_end ?? null,
  paidCount: response.paid_count ?? 0,
  cancelledOn: response.cancelled_at ?? null,
  refund: toRefund(response.refund),
});

/**
 * The billing API's subscription record, as every screen here reads it.
 *
 * One mapper for both the buy and the read, because it is one record - the
 * purchase hands it back and `GET /billing/subscriptions/` hands the same thing
 * back later.
 *
 * Three fields the record does not carry, given the answer that is true rather
 * than a guess:
 *
 *   - **method** is `null`. It is chosen inside Razorpay's window and this
 *     endpoint does not report it; the status card is built for not knowing.
 *   - **paidCount** is 0. Nothing says how many cycles have been collected, so
 *     the invoice history is what answers that.
 *   - **refund** is `null`. There is no refund on this shape at all, which is
 *     why a refund in flight is read off `/billing/refund/` instead — see
 *     `refundService`, and `useRefundOffer`, which is what the screen holds it
 *     in.
 *
 * `amount` is what one cycle is charged with its tax on it: the quote's total
 * where the purchase has just been priced, and the plan's `gross_amount`
 * otherwise, which is the same figure the backend would invoice.
 */
export const toBillingSubscription = (
  record: BoughtSubscriptionResponse,
  amount?: string | null
): Subscription => ({
  id: record.public_id,
  // An interval this app has no card for reads as monthly - the shorter of the
  // two, so nothing over-states what somebody has committed to. The same rule
  // `toCycle` keeps for the older records.
  cycle: cycleOfInterval(record.plan?.interval) ?? "monthly",
  // What the card actually says, and both of them come off the record: the
  // plan the backend issued is the one being paid for, whether or not the
  // price list still offers it. `cycle` above cannot answer either question —
  // it is one of two cards, and this subscription may be on neither.
  planName: record.plan?.name?.trim() || null,
  cycleLabel: labelOfInterval(record.plan?.interval),
  status: toStatus(record.status),
  method: null,
  amount: toAmount(amount ?? record.plan?.gross_amount),
  currentStart: record.current_period_start ?? null,
  currentEnd: record.current_period_end ?? null,
  paidCount: 0,
  cancelledOn: record.cancel_requested_at ?? null,
  refund: null,
});

/** The record a purchase answers with, priced by the quote that came back
 *  beside it - the exact figure that order was bought at. */
export const toPurchasedSubscription = (
  response: BuySubscriptionResponse
): Subscription =>
  toBillingSubscription(response.subscription, response.quote?.total_amount);

/**
 * Buys one: the subscription the mandate will be signed against.
 *
 * No figure goes up - the plan by its code, and the coupon by its own - because
 * the price is the server's to decide, again, at the moment of purchase.
 * Whatever the summary was showing, what comes back is what this is being
 * bought at, and the `quote` on the response is that answer.
 *
 * The coupon is the one the quote was last asked with, passed on so the
 * purchase is the order that was quoted. A code the server will not honour is
 * refused here rather than silently dropped, which is the right way round: a
 * discount shown and then not charged is worse than a purchase that stops and
 * says why.
 *
 * The record comes back `PENDING_AUTH`: issued, nothing signed, nothing
 * charged. What is charged, and when, follows the mandate being authorised in
 * the gateway's window against the `subscriptionId` handed back here - and the
 * webhook behind it, not this browser, is what decides the subscription is
 * live.
 *
 * Who it is billed to is not sent: that is the billing profile, recorded before
 * this is called. See `handlePay`, which is where the order of the two is kept.
 */
export async function buySubscription(
  getToken: GetToken,
  planCode: string,
  { couponCode, signal }: BuyOptions = {}
): Promise<PurchasedSubscription> {
  // The key is left off entirely where there is no code, so an order with no
  // coupon on it is asked for exactly as it always was.
  const body: BuySubscriptionRequest = {
    plan_code: planCode,
    ...(couponCode ? { coupon_code: couponCode } : {}),
  };

  const response = await api.post<BuySubscriptionResponse>(
    API_ENDPOINTS.billing.subscriptions,
    body,
    { token: await token(getToken, "start your subscription"), signal }
  );

  const subscription = toPurchasedSubscription(response);
  // The same reading of a quote the checkout has been drawing all along, so the
  // figure on the receipt and the figure in the summary come out of one place.
  const quoted = toQuotedPrice(response.quote, { cycle: subscription.cycle });

  return {
    subscription,
    checkout: {
      key: response.checkout.key,
      subscriptionId: response.checkout.subscription_id,
      productName: response.checkout.name,
      description: response.checkout.description,
      recurring: response.checkout.recurring,
    },
    price: quoted.price,
    coupon: quoted.coupon,
  };
}

/**
 * One subscription as it now stands. This is what the checkout polls while a
 * mandate is being approved, and what a result screen confirms against — the
 * browser's own account of an authorisation is a hint, and the webhook behind
 * this endpoint is the answer.
 */
export async function fetchSubscription(
  getToken: GetToken,
  id: string,
  signal?: AbortSignal
): Promise<Subscription> {
  const response = await api.get<SubscriptionResponse>(
    API_ENDPOINTS.subscriptions.detail(id),
    { token: await token(getToken, "check your subscription"), signal }
  );

  return toSubscription(response);
}

/**
 * What the workspace is on, or `null` for one that has never subscribed.
 *
 * Nothing inside the envelope, an empty body and a 404 are all that `null`
 * rather than a failure: never having subscribed is the ordinary state of a
 * workspace before its first purchase, and the Billing screen has a screen for
 * it - the price list.
 *
 * This is the one call that answers "am I a paying customer", and it is the
 * only thing the Plan details screen draws its status card from.
 */
export async function fetchCurrentSubscription(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<Subscription | null> {
  try {
    const response = await api.get<CurrentSubscriptionResponse | null>(
      API_ENDPOINTS.billing.currentSubscription,
      { token: await token(getToken, "load your subscription"), signal }
    );

    const record = response?.subscription;
    return record ? toBillingSubscription(record) : null;
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) return null;
    throw cause;
  }
}

/**
 * Hands the gateway's account of an authorisation to the backend to be checked
 * against its own secret.
 *
 * Worth saying again, because it is the mistake that costs a week: the
 * signature the backend recomputes is over
 * `razorpay_payment_id + "|" + subscription_id` — the reverse of the order an
 * ordinary order uses. This app only carries the two values across; it is the
 * backend that must get that round the right way.
 *
 * Calling this is an optimisation. The webhook activates the subscription
 * whether or not the browser ever arrives here, which is why every screen
 * still reads the state back rather than trusting what this returned.
 */
export async function verifySubscription(
  getToken: GetToken,
  id: string,
  receipt: AuthorisationReceipt,
  signal?: AbortSignal
): Promise<Subscription> {
  const response = await api.post<SubscriptionResponse>(
    API_ENDPOINTS.subscriptions.verify(id),
    {
      razorpay_payment_id: receipt.razorpayPaymentId,
      razorpay_signature: receipt.razorpaySignature,
    },
    { token: await token(getToken, "confirm your subscription"), signal }
  );

  return toSubscription(response);
}

/**
 * The invoice for the cycle just paid for, as a PDF.
 *
 * The bytes, not a link to them — so this is `getBlob` with an `Accept` of
 * `application/pdf`, the way a meal plan's document is fetched. The backend
 * renders it, because the backend is what holds the invoice number and the
 * registered details it has to carry.
 */
export async function fetchSubscriptionInvoice(
  getToken: GetToken,
  id: string,
  signal?: AbortSignal
): Promise<Blob> {
  return api.getBlob(API_ENDPOINTS.subscriptions.invoice(id), {
    token: await token(getToken, "download your invoice"),
    headers: { Accept: "application/pdf" },
    signal,
  });
}

/**
 * The status the history table draws.
 *
 * Anything the app does not recognise reads as `EXPIRED`. That way round
 * deliberately: the other three are all claims about money or about right now,
 * and a claim is not something to make out of a string nobody understood.
 */
/** Today as an ISO day, off the calendar the reader is looking at. ISO days
 *  compare correctly as strings, so no clock arithmetic is needed. */
const isoToday = (now: Date): string =>
  [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");

/** The backend's word for where a term stands, in this app's. `CURRENT` is the
 *  one running; the other two are spelled the same on both sides. */
const TERM_STATUSES: Record<string, InvoiceStatus> = {
  SCHEDULED: "SCHEDULED",
  CURRENT: "ACTIVE",
  EXPIRED: "EXPIRED",
};

/**
 * What the badge beside an invoice number says about that row.
 *
 * It is not the invoice's own status, and the difference matters. The endpoint
 * answers `PAID`, which is about the money: it arrived. The badge asks where
 * the term this invoice bought stands today — see `InvoiceStatus`. Reading
 * `PAID` straight through as a label would put "Paid" beside a term that ran
 * out eight months ago.
 *
 * `subscription_term_status` is that answer and is taken whole where the row
 * carries it. It is the server's to give for the same reason `days_left` is:
 * terms begin and end on the IST calendar, not on the one the laptop is set
 * to — and `SCHEDULED` cannot be derived here at all. A period starting next
 * month looks identical whether it is a second term bought early or a row
 * nobody has caught up with, and only the backend knows which.
 *
 * The dates are the fallback, for a backend that has not got the field yet.
 * They answer two of the three states and never the third.
 *
 * A refund outranks both: money going back, or gone, is the most important
 * thing true of that row.
 *
 * Anything unrecognised reads as `EXPIRED`. That way round deliberately: the
 * others are claims about money or about right now, and a claim is not
 * something to make out of a string nobody understood.
 */
const toInvoiceStatus = (
  row: InvoiceResponse,
  now: Date = new Date()
): InvoiceStatus => {
  const status = (row.status ?? "").toUpperCase();

  if (status === "REFUNDED") return "REFUNDED";
  if (status === "REFUND_PENDING") return "REFUND_PENDING";

  const term = TERM_STATUSES[(row.subscription_term_status ?? "").toUpperCase()];
  if (term) return term;

  if (status === "PAID" || status === "ACTIVE") {
    const end = row.period_end ?? null;
    // No period to judge by: the charge stands, and the safer of the two is the
    // one that claims nothing about now.
    return end && end >= isoToday(now) ? "ACTIVE" : "EXPIRED";
  }

  return "EXPIRED";
};

/**
 * One row, as the table draws it.
 *
 * Six fields out of twenty. The rest of the row is what the *document* is
 * built from - the breakdown, the period, the payment behind it - and the
 * document is the backend's to render, which is why the table shows what was
 * charged and offers the PDF rather than restating an invoice in HTML.
 *
 * The plan and its term are two of the six because a prepaid history is a list
 * of separate purchases rather than one plan charged over and over: what was
 * bought, and for how long, is the difference between two rows that otherwise
 * read as the same ₹499 twice.
 */
const toInvoice = (response: InvoiceResponse): SubscriptionInvoice => ({
  id: response.public_id,
  number: response.invoice_number ?? "",
  // Empty strings read as "the row did not say", the same as a missing key:
  // the table draws nothing either way, and a blank cell is better than a
  // separator with nothing on one side of it.
  planName: response.plan_name || null,
  planDuration: response.plan_duration || null,
  // The charged figure, tax included - what was actually taken, not what the
  // plan lists at.
  amount: toAmount(response.total_amount),
  issuedOn: response.issued_at ?? "",
  status: toInvoiceStatus(response),
});

/**
 * Every invoice the workspace has been raised, newest first.
 *
 * DRF's page envelope, and every row of it: the whole run is asked for rather
 * than a page at a time, because the table draws them all and a practice's
 * invoices are counted in cycles rather than in thousands. An array is read too,
 * in case the endpoint ever answers as one.
 *
 * Sorted here rather than trusted from the wire: the table is read top-down as
 * "what happened most recently", and a page whose order depends on how the
 * backend happened to build its queryset is a page that reorders itself the
 * day someone adds an index.
 */
export async function fetchSubscriptionInvoices(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<SubscriptionInvoice[]> {
  const response = await api.get<
    InvoiceResponse[] | PaginatedResponse<InvoiceResponse> | null
  >(API_ENDPOINTS.billing.invoices, {
    token: await token(getToken, "load your billing history"),
    signal,
  });

  const rows = Array.isArray(response) ? response : response?.results ?? [];

  return rows
    .map(toInvoice)
    .sort(
      (a, b) =>
        new Date(b.issuedOn).getTime() - new Date(a.issuedOn).getTime()
    );
}

/**
 * One invoice's PDF, for the download at the end of its row.
 *
 * `GET /billing/invoices/{public_id}/pdf/`, by the id the list on that same
 * `/billing/invoices/` carries — so the row and its document are read off one
 * record, and no id is interpolated into a URL at this call site.
 *
 * The bytes, not a link to them, which is why this is `getBlob` rather than a
 * `get`. What comes back is the backend's rendering: the invoice number, the
 * tax split and the registered details are all its to hold, and a copy drawn in
 * the browser would be a second document for one payment.
 */
export async function fetchInvoiceDocument(
  getToken: GetToken,
  invoiceId: string,
  signal?: AbortSignal
): Promise<Blob> {
  return api.getBlob(API_ENDPOINTS.billing.invoicePdf(invoiceId), {
    token: await token(getToken, "download your invoice"),
    // A PDF for preference, and anything rather than nothing — the same header
    // `fetchMealPlanPdf` asks with, for the same reason.
    //
    // `application/pdf` alone is refused: DRF negotiates the `Accept` before
    // the view runs, finds no renderer registered for that type, and answers
    // 406 `not_acceptable` — "Could not satisfy the request Accept header".
    // This view writes the file itself rather than going through a renderer, so
    // the `*/*` is only there to give that negotiation something to land on;
    // what comes back is the document either way.
    headers: { Accept: "application/pdf, */*" },
    signal,
  });
}

/**
 * Hands a downloaded invoice to the browser.
 *
 * The object URL is released straight after the click — the download has
 * already been handed off by then, and holding it would keep the whole
 * document in memory for the life of the tab. The same shape as
 * `savePlanPdf`, which does this for a meal plan.
 */
export const saveInvoicePdf = (invoice: Blob, fileName: string): void => {
  const url = URL.createObjectURL(invoice);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
};

/**
 * Stops it renewing.
 *
 * Nothing goes up — not an id, not a flag. There is one subscription per
 * workspace and the bearer token says whose; and the plan running to the day it
 * is paid up to is the backend's policy rather than something asked for from
 * here. It used to travel as `at_cycle_end: true`, which made a promise the
 * cancel dialog has already made look like a browser's choice.
 *
 * What comes back needs reading with care: the record is still `ACTIVE`, its
 * period end is unmoved, and the only thing that changed is
 * `cancel_requested_at` — which `toBillingSubscription` maps to `cancelledOn`.
 * That field, and not the status, is what says the plan is on its way out; the
 * status turns `CANCELLED` on the day the cycle actually ends.
 */
export async function cancelSubscription(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<Subscription> {
  const response = await api.post<CancelSubscriptionResponse>(
    API_ENDPOINTS.billing.cancelSubscription,
    // No body: the endpoint takes none, and `{}` is what an empty POST sends.
    {},
    { token: await token(getToken, "cancel your subscription"), signal }
  );

  return toBillingSubscription(response.subscription);
}
